import React, { useEffect, useRef, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import {
  convertDisplayToUSD,
  convertUSDToDisplay,
  getCurrencySymbolByCode,
  roundDisplayAmountForCurrency,
} from '../../../../utils/currencyUtils';

const formatForInput = (usdValue, effectiveCurrency, currencyRates, { ceilDisplay = false } = {}) => {
  const display = convertUSDToDisplay(Number(usdValue || 0), effectiveCurrency, currencyRates);
  if (!display) return '0';
  if (ceilDisplay) {
    const rounded = roundDisplayAmountForCurrency(display, effectiveCurrency);
    const fractionDigits = effectiveCurrency === 'USD' || effectiveCurrency === 'EUR' ? 1 : 0;
    return rounded.toFixed(fractionDigits).replace(/\.0$/, '');
  }
  const rounded = Number(display).toFixed(2);
  return rounded.replace(/\.?0+$/, '');
};

const PhoneNumberAmountInput = ({
  usdValue,
  effectiveCurrency,
  currencyRates,
  onCommit,
  ceilDisplay = false,
}) => {
  const [raw, setRaw] = useState(() => formatForInput(usdValue, effectiveCurrency, currencyRates, { ceilDisplay }));
  const focusedRef = useRef(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    setRaw(formatForInput(usdValue, effectiveCurrency, currencyRates, { ceilDisplay }));
  }, [usdValue, effectiveCurrency, currencyRates, ceilDisplay]);

  const symbol = getCurrencySymbolByCode(effectiveCurrency);

  const commit = () => {
    const trimmed = raw === '' ? '' : Number(raw);
    if (raw === '' || isNaN(trimmed) || trimmed < 0) {
      onCommit(0);
      setRaw(formatForInput(0, effectiveCurrency, currencyRates, { ceilDisplay }));
      dirtyRef.current = false;
      return;
    }
    const usd = convertDisplayToUSD(trimmed, effectiveCurrency, currencyRates);
    onCommit(usd);
    setRaw(formatForInput(usd, effectiveCurrency, currencyRates, { ceilDisplay }));
    dirtyRef.current = false;
  };

  return (
    <TextField
      value={raw}
      onChange={(e) => {
        dirtyRef.current = true;
        setRaw(e.target.value);
      }}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => {
        focusedRef.current = false;
        if (dirtyRef.current) {
          commit();
          return;
        }
        setRaw(formatForInput(usdValue, effectiveCurrency, currencyRates, { ceilDisplay }));
      }}
      type="number"
      size="small"
      variant="standard"
      sx={{
        width: 64,
        '.MuiInputBase-input': { py: 0.5, textAlign: 'center', fontWeight: 500, height: 32, transition: 'background-color 0.2s' },
        '.MuiInputBase-root': { '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' } },
      }}
      InputProps={{
        disableUnderline: true,
        style: { textAlign: 'center' },
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0.25, fontSize: 14, color: 'text.secondary' }}>
            {symbol}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default PhoneNumberAmountInput;
